import Vue from 'vue';
import uniqid from 'uniqid';

import { get, cloneDeep, round, removeKey } from '@/helpers';
import { getShippingFee } from '@/extensions/shop/helpers';

import ShopApi from '@/extensions/shop/store/api/shop';
// import apolloClient from '@/store/apollo';

// initial state
const state = {
    preferredListOrientation: localStorage.getItem('product-list-orientation'), // 'horizontal' | 'vertical' | 'justified'
    cart: {
        initialised: false,
        articlePivots: [],
        qty: 0,
        tax: 0,
        sum: 0
    },
    checkout: {
        position: 1, // TODO: change back to `1`
        initialised: false,
        wait: false, // prevent submitting while transition animation leads to valid
        saving: false,
        success: false,
        steps: [
            {
                key: 'cart',
                position: 1,
                label: 'Warenkorb',
                icon: 'shopping-cart'
            },
            {
                key: 'account',
                position: 2,
                label: 'Benutzerdaten',
                icon: 'user'
            },
            {
                key: 'shipping',
                position: 3,
                label: 'Versand',
                icon: 'dolly-flatbed'
            },
            {
                key: 'payment',
                position: 4,
                label: 'Bestellen',
                icon: 'money-bill'
            }
        ],
        form: {
            account: {
                // will get a purchaser
                id: undefined, // member id: undefined -> guest
                create: false,
                gender: 'M',
                first_name: '',
                last_name: '',
                mobile: '',
                phone: '',
                email: '',
                password: '',
                billing_address: {
                    // default_billing address data from member
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    company: '',
                    street: '',
                    street_addition: '',
                    zipcode: '',
                    city: '',
                    country: '', // ISO-2 code
                    type: 'billing',
                    latitude: undefined,
                    longitude: undefined
                },
                shipping_address: {
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    company: '',
                    street: '',
                    street_addition: '',
                    zipcode: '',
                    city: '',
                    country: '', // ISO-2 code
                    type: 'billing',
                    latitude: undefined,
                    longitude: undefined
                }
            },
            payment: {
                id: undefined // payment method
            },
            shipping: {
                id: undefined // shipping method
            }
        }
    },
    product: undefined,
    customer: undefined // TODO: null
};

// getters
const getters = {
    id: (state, getters, rootState, rootGetters) => get(rootGetters, 'CMS.shop.id'),
    cart: state => state.cart,
    product: state => state.product,
    customer: state => state.customer,
    checkout: state => state.checkout,
    addressOptions: (state, getters, rootState) => {
        let addressOptions = {
            billing: [],
            shipping: []
        };

        // find default addresses
        let billingAddress, shippingAddress;
        const addresses = get(rootState.auth, 'member.addresses') || [];

        addresses.forEach(address => {
            if (address.default_billing && !billingAddress) billingAddress = address;
            if (address.default_shipping && !shippingAddress) shippingAddress = address;
        });

        if (!billingAddress || !shippingAddress) {
            addresses.forEach(address => {
                if (address.type === 'billing' && !billingAddress) billingAddress = address;
                if (address.type === 'shipping' && !shippingAddress) shippingAddress = address;
            });
        }

        if (!billingAddress) billingAddress = addresses.find(address => address.type === 'main');
        if (!shippingAddress) shippingAddress = addresses.find(address => address.type === 'main');

        if (billingAddress) addressOptions.billing.push(billingAddress);
        if (shippingAddress) addressOptions.shipping.push(shippingAddress);

        const additionalBillingAddresses = addresses.find(address => {
            return (
                ['main', 'billing', 'additional'].includes(address.type) &&
                address.id !== get(shippingAddress, 'id') &&
                address.id !== get(billingAddress, 'id')
            );
        });
        if (additionalBillingAddresses)
            addressOptions.billing = [...addressOptions.billing, additionalBillingAddresses];

        const additionalShippingAddresses = addresses.find(address => {
            return (
                ['main', 'shipping', 'additional'].includes(address.type) &&
                address.id !== get(shippingAddress, 'id') &&
                address.id !== get(billingAddress, 'id')
            );
        });
        if (additionalShippingAddresses)
            addressOptions.shipping = [...addressOptions.shipping, additionalShippingAddresses];

        return addressOptions;
    },
    shippingMethodOptions: (state, getters, rootState, rootGetters) => {
        const shippingMethods = get(rootGetters, 'CMS.shop.shippingMethods') || [];

        const freeShipping = shippingMethods.find(el => el.service === 'free');
        const remainingShippingMethods = shippingMethods.filter(el => {
            if (freeShipping && freeShipping.id === el.id) return false;
            return el;
        });

        let options;
        if (freeShipping && remainingShippingMethods.length) {
            options = [freeShipping, ...remainingShippingMethods];
        } else if (remainingShippingMethods.length) {
            options = remainingShippingMethods;
        } else if (freeShipping) {
            options = [freeShipping];
        }

        return options;
    },
    shippingFee: (state, getters) => {
        let fee = 0;
        const shippingMethod = get(getters.checkout.form, 'shipping');
        if (shippingMethod?.id && state.cart.qty > 0) {
            fee = getShippingFee(shippingMethod);
        }
        return fee;
    },
    defaultCustomerGroup: (state, getters, rootState, rootGetters) => {
        return get(rootGetters, 'CMS.shop.defaultCustomerGroup');
    },
    defaultTax: (state, getters, rootState, rootGetters) => {
        return get(rootGetters, 'CMS.shop.defaultTax');
    },
    preferredListOrientation: state => state.preferredListOrientation
};

// mutations
const mutations = {
    setPreferredListOrientation(state, value) {
        if (['horizontal', 'vertical', 'justified'].includes(value)) {
            state.preferredListOrientation = value;
            localStorage.setItem('product-list-orientation', value);
        }
    },
    setProduct(state, product) {
        Vue.set(state, 'product', Object.freeze(product));
    },
    setCustomer(state, customer) {
        Vue.set(state, 'customer', Object.freeze(customer));
    },
    setCart(state, cart) {
        // check availability of all articlePivots
        // if stock !== null (manageStock)
        // update `available` and `qty_at_limit`

        if (cart.articlePivots && cart.articlePivots.length) {
            let qtyMap = {};
            cart.articlePivots.forEach(articlePivot => {
                if (!qtyMap[articlePivot.article.id]) {
                    qtyMap[articlePivot.article.id] = {
                        stock: articlePivot.stock,
                        cart: 0
                    };
                }

                qtyMap[articlePivot.article.id].cart += articlePivot.qty;
            });

            cart.articlePivots.forEach(articlePivot => {
                if (articlePivot.stock !== null) {
                    articlePivot.available =
                        qtyMap[articlePivot.article.id].stock >= qtyMap[articlePivot.article.id].cart;
                    articlePivot.qty_at_limit =
                        qtyMap[articlePivot.article.id].stock === qtyMap[articlePivot.article.id].cart;
                }
            });
        }
        Vue.set(state, 'cart', cart);
    },
    setCartInitialised(state, status) {
        Vue.set(state.cart, 'initialised', status);
    },
    setCheckoutInitialised(state, status) {
        Vue.set(state.checkout, 'initialised', status);
    },
    setCheckoutSaving(state, value) {
        Vue.set(state.checkout, 'saving', value);
    },
    setCheckoutSuccess(state, value) {
        Vue.set(state.checkout, 'success', value);
    },
    setCheckoutPosition(state, value) {
        Vue.set(state.checkout, 'position', value);
    },
    setCheckoutFormAccount(state, account) {
        Vue.set(state.checkout.form, 'account', account);
    },
    setCheckoutFormAccountField(state, { field, value }) {
        Vue.set(state.checkout.form.account, field, value);
    },
    setCheckoutFormBillingAddress(state, address) {
        const billingAddress = {
            id: address.id,
            gender: null,
            first_name: null,
            last_name: null,
            company: null,
            street: address.street,
            street_addition: address.street_addition,
            zipcode: address.zipcode,
            city: address.city,
            country: address.country,
            type: 'billing',
            latitude: address.latitude,
            longitude: address.longitude
        };

        Vue.set(state.checkout.form.account, 'billing_address', billingAddress);
    },
    setCheckoutFormBillingAddressField(state, { field, value }) {
        Vue.set(state.checkout.form.account.billing_address, field, value);
    },
    setCheckoutFormShippingAddress(state, shippingAddress) {
        Vue.set(state.checkout.form.account, 'shipping_address', shippingAddress);
    },
    setCheckoutFormShippingAddressField(state, { field, value }) {
        Vue.set(state.checkout.form.account.shipping_address, field, value);
    },
    setCheckoutFormShipping(state, value) {
        Vue.set(state.checkout.form, 'shipping', value);
    },
    setCheckoutFormPayment(state, value) {
        Vue.set(state.checkout.form, 'payment', value);
    }
};

// actions
const actions = {
    fetchProductsPaginated(context, payload) {
        return ShopApi.fetchProductsPaginated(payload);
    },
    fetchProductsWithArticlesPaginated(context, payload) {
        return ShopApi.fetchProductsPaginated(payload, true);
    },
    fetchProduct(context, payload) {
        return ShopApi.fetchProduct(payload);
    },
    saveCart({ state, rootGetters }) {
        ShopApi.saveShoppingCartLocal(state.cart.articlePivots);

        if (rootGetters['auth/isAuthenticated']) {
            const articlePivots = removeKey(cloneDeep(state.cart.articlePivots), '__typename');
            ShopApi.debouncedSaveShoppingCart(articlePivots);
            // .then(cart => {
            //   // save locally and add to state
            //   ShopApi.saveShoppingCartLocal(cart.articlePivots);
            //   commit('setCart', cart);
            // });
        }
    },
    addArticleToCart({ commit, dispatch, state, getters }, payload) {
        const article = payload.article;
        if (!article) throw new Error('missing article');

        let qty = payload.qty || 1;

        let propertyValues = [];
        let productPropertyValues = [];

        if (payload.propertyValues) propertyValues = payload.propertyValues;
        else if (payload.productPropertyValues) productPropertyValues = payload.productPropertyValues;

        if (!propertyValues.length) {
            article.propertyPivots.forEach(pivot => propertyValues.push({ id: pivot.value.id }));
            if (productPropertyValues.length) productPropertyValues.forEach(id => propertyValues.push({ id }));

            if (!propertyValues.length && (article.propertyPivots.length || productPropertyValues.length)) {
                throw new Error('missing property values');
            }
        }

        // handle selectable product properties
        let articlePivots = cloneDeep(state.cart.articlePivots) || [];
        const index = articlePivots.findIndex(el => {
            const diff = el.propertyValues.filter(value => !propertyValues.some(v => v.id === value.id));
            return el.article.id === article.id && !diff.length;
        });

        // TODO
        // check if manageStock
        // and if qty fits

        if (index >= 0) {
            articlePivots[index].qty += qty;
        } else {
            articlePivots.push({
                article: { id: article.id },
                propertyValues,
                qty
            });
        }

        // const defaultCustomerGroup = get(getters, 'defaultCustomerGroup');
        const defaultTax = get(getters, 'defaultTax');
        // const customer = get(state, 'customer');

        const price = article.price;
        // findPrice(article, customer, defaultCustomerGroup, true) ||
        // findPrice(article.product, customer, defaultCustomerGroup, true);

        const taxRate = get(price, 'tax.value') || defaultTax.value;
        const tax = round((price.value * taxRate) / (100 + taxRate), 2);

        const cart = {
            qty: state.cart.qty + qty,
            sum: state.cart.sum + price.value,
            tax: state.cart.tax + tax,
            articlePivots
        };

        commit('setCart', cart);
        dispatch('saveCart');
    },
    removeArticleFromCart({ commit, dispatch, state, getters }, payload) {
        const article = payload.article;
        if (!article) throw new Error('missing article');

        let qty = payload.qty || 1;

        let propertyValues = [];
        let productPropertyValues = [];

        if (payload.propertyValues) propertyValues = payload.propertyValues;
        else if (payload.productPropertyValues) productPropertyValues = payload.productPropertyValues;

        if (!propertyValues.length) {
            article.propertyPivots.forEach(pivot => propertyValues.push({ id: pivot.value.id }));
            if (productPropertyValues.length) productPropertyValues.forEach(id => propertyValues.push({ id }));

            if (!propertyValues.length && (article.propertyPivots.length || productPropertyValues.length)) {
                throw new Error('missing property values');
            }
        }

        // handle selectable product properties
        let articlePivots = cloneDeep(state.cart.articlePivots) || [];
        const index = articlePivots.findIndex(el => {
            const diff = el.propertyValues.filter(value => !propertyValues.some(v => v.id === value.id));
            return el.article.id === article.id && !diff.length;
        });

        if (index >= 0) {
            qty = Math.min(qty, articlePivots[index].qty);
            articlePivots[index].qty -= qty;

            if (articlePivots[index].qty <= 0) articlePivots.splice(index, 1);

            // const defaultCustomerGroup = get(getters, 'defaultCustomerGroup');
            const defaultTax = get(getters, 'defaultTax');
            // const customer = get(state, 'customer');

            const price = article.price;
            // findPrice(article, customer, defaultCustomerGroup, true) ||
            // findPrice(article.product, customer, defaultCustomerGroup, true);

            const taxRate = get(price, 'tax.value') || defaultTax.value;
            const tax = round((price.value * taxRate) / (100 + taxRate), 2);

            const cart = {
                qty: state.cart.qty - qty,
                sum: state.cart.sum - price.value,
                tax: state.cart.tax - tax,
                articlePivots
            };

            commit('setCart', cart);
            dispatch('saveCart');
        }
    },
    updateArticleQtyInCart({ commit, dispatch, state, getters }, payload) {
        const article = payload.article;
        if (!article) throw new Error('missing article');

        let qty = payload.qty;

        let propertyValues = [];
        let productPropertyValues = [];

        if (payload.propertyValues) propertyValues = payload.propertyValues;
        else if (payload.productPropertyValues) productPropertyValues = payload.productPropertyValues;

        if (!propertyValues.length) {
            article.propertyPivots.forEach(pivot => propertyValues.push({ id: pivot.value.id }));
            if (productPropertyValues.length) productPropertyValues.forEach(id => propertyValues.push({ id }));

            if (!propertyValues.length) throw new Error('missing property values');
        }

        // handle selectable product properties
        let articlePivots = cloneDeep(state.cart.articlePivots) || [];
        const index = articlePivots.findIndex(el => {
            const diff = el.propertyValues.filter(value => !propertyValues.some(v => v.id === value.id));
            return el.article.id === article.id && !diff.length;
        });

        qty = parseInt(qty);
        if (isNaN(qty)) qty = 0;
        else if (qty > 1000) qty = 1000;

        let q = qty;
        if (index >= 0) {
            q = qty - articlePivots[index].qty;
            articlePivots[index].qty = qty;

            if (articlePivots[index].qty <= 0) articlePivots.splice(index, 1);
        } else if (qty > 0) {
            articlePivots.push({
                article: { id: article.id },
                propertyValues,
                qty
            });
        }

        // const defaultCustomerGroup = get(getters, 'defaultCustomerGroup');
        const defaultTax = get(getters, 'defaultTax');
        // const customer = get(state, 'customer');

        const price = article.price;
        // findPrice(article, customer, defaultCustomerGroup, true) ||
        // findPrice(article.product, customer, defaultCustomerGroup, true);

        const taxRate = get(price, 'tax.value') || defaultTax.value;
        const tax = round((price.value * taxRate) / (100 + taxRate), 2);

        const cart = {
            qty: state.cart.qty + q,
            sum: state.cart.sum + price.value * q,
            tax: state.cart.tax + tax * q,
            articlePivots
        };

        commit('setCart', cart);
        dispatch('saveCart');
    },
    initCart(context, member = null) {
        const { commit, rootGetters, state } = context;

        if (!state.cart.initialised) {
            const customer = get(member, 'customer');
            if (customer) commit('setCustomer', customer);

            const articlePivots = ShopApi.getShoppingCartLocal();
            const data = get(customer, 'shoppingCart');

            // if member is logged in
            // get data from customer
            if (data && data.articlePivots && data.articlePivots.length) {
                const cart = {
                    sum: get(data, 'sum') || 0,
                    qty: get(data, 'qty') || 0,
                    tax: get(data, 'tax') || 0,
                    articlePivots: removeKey(cloneDeep(data.articlePivots), '__typename')
                };
                commit('setCart', cart);
                ShopApi.saveShoppingCartLocal(cart.articlePivots);
            }

            // otherwise initialise shopping cart by local values
            else if (articlePivots && articlePivots.length) {
                ShopApi.fetchShoppingCart(articlePivots).then(data => {
                    const cart = {
                        sum: get(data, 'sum') || 0,
                        qty: get(data, 'qty') || 0,
                        tax: get(data, 'tax') || 0,
                        articlePivots: removeKey(cloneDeep(data.articlePivots), '__typename')
                    };
                    commit('setCart', cart);

                    // if is authenticated
                    // and server returned no cart for customer
                    // but there are articles in the guest cart
                    // add the guest cart articles to the customer cart
                    // save the customer cart
                    // and clear the guest cart
                    if (rootGetters['auth/isAuthenticated']) {
                        ShopApi.saveShoppingCart(cart.articlePivots);
                        // ShopApi.saveShoppingCartLocal([], 'guest');
                    }

                    ShopApi.saveShoppingCartLocal(cart.articlePivots);
                });
            } else {
                // empty cart
                const cart = {
                    sum: 0,
                    qty: 0,
                    tax: 0,
                    articlePivots: []
                };
                commit('setCart', cart);
            }

            commit('setCartInitialised', true);
        }
    },
    clearCart({ commit, dispatch }) {
        commit('setCart', {
            articlePivots: [],
            qty: 0,
            sum: 0,
            tax: 0
        });
        dispatch('saveCart');
    },
    initCheckoutForm({ state, getters, rootGetters, commit }) {
        let account = cloneDeep(state.checkout.form.account);
        const member = rootGetters['auth/member'];

        account.id = undefined;
        account.billing_address.id = undefined;
        account.shipping_address.id = undefined;

        // init form
        if (rootGetters['auth/isAuthenticated'] && member && member.id) {
            account.id = member.id;
            account.gender = member.gender;
            account.first_name = member.first_name;
            account.last_name = member.last_name;
            account.phone = member.phone;
            account.email = member.email;

            // get default addresses from state

            if (getters.addressOptions.billing.length) {
                const billingAddress = getters.addressOptions.billing[0];
                account.billing_address = {
                    id: billingAddress.id,
                    gender: billingAddress.gender,
                    first_name: billingAddress.first_name,
                    last_name: billingAddress.last_name,
                    company: billingAddress.company,
                    street: billingAddress.street,
                    street_addition: billingAddress.street_addition,
                    zipcode: billingAddress.zipcode,
                    city: billingAddress.city,
                    country: billingAddress.country,
                    type: 'billing',
                    latitude: billingAddress.latitude,
                    longitude: billingAddress.longitude
                };
            }

            if (getters.addressOptions.shipping.length) {
                const shippingAddress = getters.addressOptions.shipping[0];
                account.shipping_address = {
                    id: shippingAddress.id,
                    gender: shippingAddress.gender,
                    first_name: shippingAddress.first_name,
                    last_name: shippingAddress.last_name,
                    company: shippingAddress.company,
                    street: shippingAddress.street,
                    street_addition: shippingAddress.street_addition,
                    zipcode: shippingAddress.zipcode,
                    city: shippingAddress.city,
                    country: shippingAddress.country,
                    type: 'shipping',
                    latitude: shippingAddress.latitude,
                    longitude: shippingAddress.longitude
                };
            }
        } else {
            // account.billing_address.id = uniqid('new:')
            // account.shipping_address.id = uniqid('new:')
        }

        commit('setCheckoutFormAccount', account);

        // set default shipping method

        let shipping = cloneDeep(state.checkout.form.shipping);
        if (!shipping.id && getters?.shippingMethodOptions?.length) {
            commit('setCheckoutFormShipping', getters.shippingMethodOptions[0]);
        }
    },
    submitCheckoutForm({ state, commit, dispatch }, paymentGatewayResponseId = null) {
        if (state.checkout.wait) {
            const errorMessage = {
                id: uniqid(),
                // title: 'Lucky Look',
                // text: 'Schneller als der eigene Schatten',
                title: 'Sie sind zu schnell',
                text: 'Versuchen Sie es nochmal.',
                type: 'warning',
                autoclose: 4000
            };
            dispatch('message/add', errorMessage, { root: true });
        } else {
            if (state.cart.articlePivots && state.cart.articlePivots.length) {
                // this.saving = true;
                commit('setCheckoutSaving', true);

                let items = [];
                state.cart.articlePivots.forEach(pivot => {
                    const item = {
                        qty: pivot.qty,
                        article: {
                            id: pivot.article.id
                        },
                        propertyValues: pivot.propertyValues.map(pv => ({ id: pv.id }))
                    };
                    items.push(item);
                });

                const payload = {
                    purchaser: {
                        gender: get(state.checkout, 'form.account.gender'),
                        first_name: get(state.checkout, 'form.account.first_name'),
                        last_name: get(state.checkout, 'form.account.last_name'),
                        email: get(state.checkout, 'form.account.email'),
                        phone: get(state.checkout, 'form.account.phone'),
                        mobile: get(state.checkout, 'form.account.mobile'),
                        billingAddress: get(state.checkout, 'form.account.billing_address'),
                        shippingAddress: get(state.checkout, 'form.account.shipping_address'),

                        create: get(state.checkout, 'form.account.create', false),
                        password: get(state.checkout, 'form.account.password')
                    },
                    items,
                    paymentMethod: {
                        id: state.checkout.form.payment.id
                    },
                    shippingMethod: {
                        id: state.checkout.form.shipping.id
                    }
                };

                if (paymentGatewayResponseId) payload.payment_gateway_response_id = paymentGatewayResponseId;

                // NOTE: don't always clear the cart
                // paypal, stripe, ...
                return ShopApi.createPurchase(payload)
                    .then(data => {
                        if (data.errors === null && data.id && data.id > 0) {
                            switch (data.paymentMethod.service) {
                                case 'paypal':
                                case 'stripe':
                                    // TODO
                                    break;

                                default:
                                    dispatch('clearCart');
                                    commit('setCheckoutPosition', 1);
                                    dispatch(
                                        'message/add',
                                        {
                                            id: uniqid(),
                                            title: 'Erfolg',
                                            text: 'Ihre Bestellung wurde erfolgreich übermittelt.',
                                            type: 'success',
                                            autoclose: 4000
                                        },
                                        { root: true }
                                    );
                                    break;
                            }
                        } else {
                            // dispatch('message/add', {
                            //   id: uniqid(),
                            //   title: 'Fehler',
                            //   text: 'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                            //   type: 'error',
                            //   autoclose: 8000
                            // }, { root: true });
                        }

                        return data;
                    })
                    .finally(() => {
                        // this.saving = false
                        commit('setCheckoutSaving', false);
                    });

                // return dispatch('createPurchase', data)
                //   .then(() => {
                //     console.log(1)
                //     this.success = true;
                //     dispatch('message/add', {
                //       id: uniqid(),
                //       title: 'Erfolg',
                //       text: 'Ihre Bestellung wurde erfolgreich übermittelt.',
                //       type: 'success',
                //       autoclose: 4000
                //     }, { root: true });
                //   })
                //   .finally(() => {
                //     console.log(2)
                //     // this.saving = false
                //     commit('setCheckoutSaving', false)
                //   });
            }
        }
    },
    approvePayment({ dispatch, commit }, id) {
        commit('setCheckoutSaving', true);

        const purchase = { id };
        return ShopApi.approvePayment(purchase)
            .then(() => {
                dispatch(
                    'message/add',
                    {
                        id: uniqid(),
                        title: 'Erfolg',
                        text: 'Ihre Bestellung wurde erfolgreich übermittelt.',
                        type: 'success',
                        autoclose: 4000
                    },
                    { root: true }
                );
                dispatch('clearCart');
                commit('setCheckoutPosition', 1);
            })
            .catch(() => {
                dispatch(
                    'message/add',
                    {
                        id: uniqid(),
                        title: 'Fehler',
                        text:
                            'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                        type: 'error',
                        autoclose: 8000
                    },
                    { root: true }
                );
            })
            .finally(() => {
                commit('setCheckoutSaving', false);
            });
    },
    createPurchase({ dispatch }, payload) {
        return ShopApi.createPurchase(removeKey(cloneDeep(payload), '__typename')).then(() => dispatch('clearCart'));
    }
};

export default {
    namespaced: true,

    state,
    getters,
    actions,
    mutations
};
